import { posthog } from "./posthog";

function track(event: string, properties?: Record<string, unknown>) {
	posthog?.capture(event, properties);
}

// Auth
export const trackUserLoggedIn = (method: string) =>
	track("user_logged_in", { method });
export const trackUserSignedUp = (method: string) =>
	track("user_signed_up", { method });

// Contacts
export const trackCsvImported = (rowCount: number) =>
	track("csv_imported", { row_count: rowCount });
export const trackContactEnriched = (matchRate: number) =>
	track("contact_enriched", { match_rate: matchRate });

// Action Groups
export const trackActionGroupCreated = (type: string) =>
	track("action_group_created", { group_type: type });
export const trackActionGroupFiltered = (filterCount: number) =>
	track("action_group_filtered", { filter_count: filterCount });

// Strategy
export const trackStrategyGenerated = (strategyType: string) =>
	track("strategy_generated", { strategy_type: strategyType });
export const trackStrategyViewed = (strategyId: number) =>
	track("strategy_viewed", { strategy_id: strategyId });

// Briefing
export const trackBriefingStarted = () => track("briefing_started");
export const trackBriefingCompleted = (durationSec: number) =>
	track("briefing_completed", { duration_sec: durationSec });

// Admin
export const trackAdminAuditReviewed = (contentType: string) =>
	track("admin_audit_reviewed", { content_type: contentType });

// Tasks
export const trackTaskCreated = (taskType: string) =>
	track("task_created", { task_type: taskType });
export const trackTaskCompleted = (taskType: string) =>
	track("task_completed", { task_type: taskType });

// Platform Intro
export const trackPlatformIntroCompleted = (skipped: boolean) =>
	track("platform_intro_completed", { skipped });

// Sequential Interview
export const trackSequentialInterviewStarted = () =>
	track("sequential_interview_started");
export const trackSequentialQuestionPlayed = (
	index: number,
	category: string,
) => track("sequential_question_played", { question_index: index, category });
export const trackSequentialAnswerSubmitted = (
	index: number,
	source: "voice" | "text",
) => track("sequential_answer_submitted", { question_index: index, source });
export const trackSequentialInterviewCompleted = (totalAnswers: number) =>
	track("sequential_interview_completed", { total_answers: totalAnswers });
export const trackSequentialInterviewResumed = (questionsAnswered: number) =>
	track("sequential_interview_resumed", {
		questions_answered: questionsAnswered,
	});
export const trackSequentialAnswerSkipped = (index: number) =>
	track("sequential_answer_skipped", { question_index: index });
export const trackSequentialReviewStarted = () =>
	track("sequential_review_started");

// Ballot Access
export const trackBallotAccessLookupTriggered = () =>
	track("ballot_access_lookup_triggered");
export const trackBallotAccessRequirementsConfirmed = () =>
	track("ballot_access_requirements_confirmed");
export const trackBallotAccessPetitionFiled = () =>
	track("ballot_access_petition_filed");
export const trackBallotAccessSignerLogged = () =>
	track("ballot_access_signer_logged");
export const trackBallotAccessValidationCompleted = () =>
	track("ballot_access_validation_completed");

// Ballot Access — Field Efficiency (PRD-37)
export const trackBallotAccessImportStarted = (driveId: number) =>
	track("ballot_access_import_started", { drive_id: driveId });
export const trackBallotAccessImportCompleted = (
	driveId: number,
	rowCount: number,
) =>
	track("ballot_access_import_completed", {
		drive_id: driveId,
		row_count: rowCount,
	});
export const trackBallotAccessDedupWarningShown = (matchCount: number) =>
	track("ballot_access_dedup_warning_shown", { match_count: matchCount });
export const trackBallotAccessDedupContinueAnyway = () =>
	track("ballot_access_dedup_continue_anyway");
export const trackBallotAccessOcrSheetUploaded = (driveId: number) =>
	track("ballot_access_ocr_sheet_uploaded", { drive_id: driveId });
export const trackBallotAccessOcrSheetCommitted = (
	driveId: number,
	signerCount: number,
) =>
	track("ballot_access_ocr_sheet_committed", {
		drive_id: driveId,
		signer_count: signerCount,
	});

// TOS
export const trackTosAccepted = (tosVersion: string) =>
	track("tos_accepted", { tos_version: tosVersion });
export const trackTosCancelled = () => track("tos_cancelled");

// Onboarding
export const trackOnboardingGetStartedViewed = () =>
	track("onboarding_get_started_viewed");
export const trackOnboardingGetStartedCompleted = () =>
	track("onboarding_get_started_completed");
export const trackOnboardingModeSelected = (mode: "voice" | "text") =>
	track("onboarding_mode_selected", { mode });
export const trackOnboardingMicSetupCompleted = () =>
	track("onboarding_mic_setup_completed");
export const trackOnboardingMicSetupSkipped = () =>
	track("onboarding_mic_setup_skipped");
export const trackOnboardingFollowUpCompleted = (answeredCount: number) =>
	track("onboarding_follow_up_completed", { answered_count: answeredCount });
export const trackOnboardingFollowUpSkipped = () =>
	track("onboarding_follow_up_skipped");

// Signup
export const trackSignupInvitePageViewed = (inviteType: string | null) =>
	track("signup_invite_page_viewed", { invite_type: inviteType });
export const trackSignupMethodSelected = (method: "google" | "password") =>
	track("signup_method_selected", { method });

// Phone Verification
export const trackPhoneVerificationStarted = () =>
	track("phone_verification_started");
export const trackPhoneVerificationCompleted = () =>
	track("phone_verification_completed");

// Checkout / Billing
export const trackCheckoutPageViewed = (
	productName: string,
	priceType: string,
	amountCents: number,
) =>
	track("checkout_page_viewed", {
		product_name: productName,
		price_type: priceType,
		amount_cents: amountCents,
	});
export const trackCheckoutPlanToggled = (fromType: string, toType: string) =>
	track("checkout_plan_toggled", { from_type: fromType, to_type: toType });
export const trackCheckoutPaymentCompleted = () =>
	track("checkout_payment_completed");

// Interview Attachments
export const trackInterviewAttachmentUploaded = (
	fileType: string,
	fileSizeBytes: number,
) =>
	track("interview_attachment_uploaded", {
		file_type: fileType,
		file_size_bytes: fileSizeBytes,
	});
export const trackInterviewAttachmentsContinued = (fileCount: number) =>
	track("interview_attachments_continued", { file_count: fileCount });

// Onboarding Completion
export const trackInterviewIntroViewed = () => track("interview_intro_viewed");
export const trackOnboardingCompleted = () => track("onboarding_completed");

// Xander AI Assistant
export const trackXanderOpened = (pageKey: string) =>
	track("xander_opened", { page_key: pageKey });
export const trackXanderClosed = (pageKey: string, messagesSentCount: number) =>
	track("xander_closed", {
		page_key: pageKey,
		messages_sent_count: messagesSentCount,
	});
export const trackXanderMessageSent = (
	pageKey: string,
	messageLength: number,
	isSuggestedPrompt: boolean,
) =>
	track("xander_message_sent", {
		page_key: pageKey,
		message_length: messageLength,
		is_suggested_prompt: isSuggestedPrompt,
	});
export const trackXanderSuggestionClicked = (
	suggestionText: string,
	pageKey: string,
) =>
	track("xander_suggestion_clicked", {
		suggestion_text: suggestionText,
		page_key: pageKey,
	});
export const trackXanderTaskCreated = (
	taskCategory: string,
	taskPriority: string,
) =>
	track("xander_task_created", {
		task_category: taskCategory,
		task_priority: taskPriority,
	});
export const trackXanderLightbulbShown = (
	nbaEvaluationId: number,
	urgencyLevel: string,
) =>
	track("xander_lightbulb_shown", {
		nba_evaluation_id: nbaEvaluationId,
		urgency_level: urgencyLevel,
	});
export const trackXanderDraftCopied = (contentType: string) =>
	track("xander_draft_copied", { content_type: contentType });
export const trackXanderHistoryOpened = (
	pageKey: string,
	sessionCount: number,
) =>
	track("xander_history_opened", {
		page_key: pageKey,
		session_count: sessionCount,
	});
export const trackXanderHistorySessionSelected = (
	sessionId: number,
	sessionAgeDays: number,
) =>
	track("xander_history_session_selected", {
		session_id: sessionId,
		session_age_days: sessionAgeDays,
	});
export const trackXanderNewChatCreated = (
	pageKey: string,
	previousSessionMessageCount: number,
) =>
	track("xander_new_chat_created", {
		page_key: pageKey,
		previous_session_message_count: previousSessionMessageCount,
	});
export const trackXanderActionConfirmed = (
	actionType: string,
	pageKey: string,
) =>
	track("xander_action_confirmed", {
		action_type: actionType,
		page_key: pageKey,
	});
export const trackXanderActionCancelled = (
	actionType: string,
	pageKey: string,
) =>
	track("xander_action_cancelled", {
		action_type: actionType,
		page_key: pageKey,
	});

// Signup Forms
export const trackSignupFormCreated = (formSlug: string) =>
	track("signup_form_created", { form_slug: formSlug });
export const trackSignupFormPublished = (formSlug: string) =>
	track("signup_form_published", { form_slug: formSlug });
export const trackSignupFormPaused = (formSlug: string) =>
	track("signup_form_paused", { form_slug: formSlug });
export const trackSignupFormDeleted = (formSlug: string) =>
	track("signup_form_deleted", { form_slug: formSlug });
export const trackSignupFormEmbedCopied = (formSlug: string) =>
	track("signup_form_embed_copied", { form_slug: formSlug });

// Email Campaigns
export const trackEmailDeliverabilityAcknowledged = (acknowledged: boolean) =>
	track("email_deliverability_acknowledged", { acknowledged });

// Voter-File AI Query
export const trackVoterFileAIQuerySubmitted = (queryLength: number) =>
	track("voterfile_ai_query_submitted", { query_length: queryLength });
export const trackVoterFileAIQueryAnswered = (
	queryType: "lookup" | "stats",
	rowCount: number,
) =>
	track("voterfile_ai_query_answered", {
		query_type: queryType,
		row_count: rowCount,
	});
export const trackVoterFileAIQueryFailed = (message: string) =>
	track("voterfile_ai_query_failed", { message });

// Call Time (PRD-42)
export const trackCallTimeListSearched = (queryLength: number) =>
	track("call_time_list_searched", { query_length: queryLength });
export const trackCallTimeSortChanged = (sortBy: string) =>
	track("call_time_sort_changed", { sort_by: sortBy });
export const trackCallTimeRecipeApplied = (recipeName: string) =>
	track("call_time_recipe_applied", { recipe_name: recipeName });
export const trackCallTimeRecipeSaved = (
	recipeName: string,
	ruleCount: number,
) =>
	track("call_time_recipe_saved", {
		recipe_name: recipeName,
		rule_count: ruleCount,
	});

// Voter-File Stats (PRD-36)
export const trackVoterFileStatsDashboardViewed = (dashboard: string) =>
	track("voterfile_stats_dashboard_viewed", { dashboard });
export const trackVoterFileStatsQueryExecuted = (
	dashboard: string,
	groupBy: string[],
	rowCount: number,
	cached: boolean,
) =>
	track("voterfile_stats_query_executed", {
		dashboard,
		group_by: groupBy.join(","),
		row_count: rowCount,
		cached,
	});

// Voter Workflow (PRD-41 §5.10) — instrumentation for the voter portal.
// Ken Block's session showed zero events from the voter area; without
// these we cannot see users getting stuck. Field-name-only on filters,
// no values, to keep PII out of PostHog.
export const trackVoterFileSearched = (
	queryLength: number,
	resultCount: number,
	scope?: string,
) =>
	track("voter_file_searched", {
		query_length: queryLength,
		result_count: resultCount,
		scope,
	});
export const trackVoterFileFilterApplied = (
	fieldNames: string[],
	resultCount: number,
	timeToResultMs?: number,
) =>
	track("voter_file_filter_applied", {
		field_names: fieldNames,
		result_count: resultCount,
		time_to_result_ms: timeToResultMs,
	});
// l2_voter_id deliberately NOT captured — it's a stable per-voter
// identifier that would let PostHog re-identify individual voters,
// and the surrounding helpers explicitly avoid PII. We track the
// occurrence; campaign/session context is enough to measure funnels.
export const trackVoterDetailViewed = () => track("voter_detail_viewed");
export const trackVoterImportStarted = (
	estimatedCount: number,
	source: "filter" | "group" | "full",
) =>
	track("voter_import_started", {
		estimated_count: estimatedCount,
		source,
	});
export const trackVoterImportCompleted = (
	importedCount: number,
	skippedCount: number,
	erroredCount: number,
) =>
	track("voter_import_completed", {
		imported_count: importedCount,
		skipped_count: skippedCount,
		errored_count: erroredCount,
	});
export const trackVoterImportCancelled = (
	progressCount: number,
	totalCount: number,
) =>
	track("voter_import_cancelled", {
		progress_count: progressCount,
		total_count: totalCount,
	});
export const trackActionGroupUsedForOutreach = (
	groupType: string,
	channel: "text" | "phone" | "email" | "call_time",
	audienceSize: number,
) =>
	track("action_group_used_for_outreach", {
		group_type: groupType,
		channel,
		audience_size: audienceSize,
	});
export const trackTextCampaignCreated = (
	audienceSize: number,
	sourceGroupType: string,
) =>
	track("text_campaign_created", {
		audience_size: audienceSize,
		source_group_type: sourceGroupType,
	});
export const trackTextCampaignLaunchBlocked = (reason: string) =>
	track("text_campaign_launch_blocked", {
		reason,
	});
export const trackPhoneCampaignCreated = (
	audienceSize: number,
	sourceGroupType: string,
) =>
	track("phone_campaign_created", {
		audience_size: audienceSize,
		source_group_type: sourceGroupType,
	});
export const trackEmailCampaignCreated = (
	audienceSize: number,
	sourceGroupType: string,
) =>
	track("email_campaign_created", {
		audience_size: audienceSize,
		source_group_type: sourceGroupType,
	});

// Outreach (PRD 46)
export const trackOutreachWizardStepCompleted = (
	step: number,
	channel?: string,
) => track("outreach_wizard_step_completed", { step, channel });
export const trackOutreachMessageDrafted = (
	channel: string,
	tone: string,
	regenerated: boolean,
) => track("outreach_message_drafted", { channel, tone, regenerated });
export const trackOutreachAudienceCounted = (
	count: number,
	source: "built" | "saved",
) => track("outreach_audience_counted", { count, source });
export const trackOutreachCampaignLaunched = (
	channel: string,
	audienceCount: number,
	scheduleMode: string,
) =>
	track("outreach_campaign_launched", {
		channel,
		audience_count: audienceCount,
		schedule_mode: scheduleMode,
	});
export const trackOutreachCampaignDuplicated = (fromChannel?: string) =>
	track("outreach_campaign_duplicated", { from_channel: fromChannel });
// AME-529: operator sent a test message for a draft before launching.
export const trackOutreachTestSent = (channel: string, status: string) =>
	track("outreach_test_sent", { channel, status });

// Campaign Actions (PRD 53) — the OutreachGoal container over outreach actions.
// A Campaign is a grouping container with no audience of its own (Rev 11).
export const trackCampaignCreated = () => track("campaign_created", {});
export const trackCampaignActionAdded = (
	goalId: number,
	existingActionCount: number,
) =>
	track("campaign_action_added", {
		goal_id: goalId,
		existing_action_count: existingActionCount,
	});

// Outreach Smart Links (PRD 47)
export const trackSmartLinksGenerated = (channel: string, linkCount: number) =>
	track("smart_links_generated", { channel, link_count: linkCount });
export const trackSmartLinkDomainChanged = (domain: string) =>
	track("smart_link_domain_changed", { domain });

// Volunteers (PRD-43)
export const trackVolunteerImported = (rowCount: number) =>
	track("volunteer_imported", { row_count: rowCount });
export const trackVolunteerFilterApplied = (filterCount: number) =>
	track("volunteer_filter_applied", { filter_count: filterCount });
export const trackVolunteerEmailSent = (audienceSize: number) =>
	track("volunteer_email_sent", { audience_size: audienceSize });
export const trackVolunteerFulfillmentUpdated = (field: string) =>
	track("volunteer_fulfillment_updated", { field });
export const trackVolunteerInviteLinkCopied = () =>
	track("volunteer_invite_link_copied");

// Prepaid Billing (PRD 45)
// Phase 1 wires `trackBillingViewed` (billing page mount) and
// `trackBillingUsageExported` (CSV export). The remaining helpers are
// stubbed here so Phases 2–4 (top-up, auto-reload, send-gating) can wire
// them without re-touching this file.
export const trackBillingViewed = (tier: string, balanceMicros: number) =>
	track("billing_viewed", { tier, balance_micros: balanceMicros });
export const trackBillingUsageExported = (rowCount: number) =>
	track("billing_usage_exported", { row_count: rowCount });
export const trackBillingTopUpStarted = (amountMicros: number) =>
	track("billing_top_up_started", { amount_micros: amountMicros });
export const trackBillingTopUpCompleted = (amountMicros: number) =>
	track("billing_top_up_completed", { amount_micros: amountMicros });
export const trackBillingTopUpFailed = (reason: string) =>
	track("billing_top_up_failed", { reason });
export const trackBillingAutoReloadSaved = (
	amountMicros: number,
	thresholdMicros: number,
) =>
	track("billing_auto_reload_saved", {
		amount_micros: amountMicros,
		threshold_micros: thresholdMicros,
	});
export const trackBillingLowBalanceWarningShown = (
	balanceMicros: number,
	estCostMicros: number,
) =>
	track("billing_low_balance_warning_shown", {
		balance_micros: balanceMicros,
		est_cost_micros: estCostMicros,
	});
export const trackBillingOutOfCreditsBlocked = (channel: "text" | "phone") =>
	track("billing_out_of_credits_blocked", { channel });
